/**
 * Pages/Panel/PreNomina/SobreRecibo/desgloseExport.js
 *
 * PDF, impresión y Excel del desglose fiscal (ISR / IMSS) del sobre-recibo.
 *
 * Los tres leen la MISMA estructura que pinta ModalDesgloseFiscal —la que arma
 * `desglose.utils.js` en el backend—, así que una sección nueva en el servidor sale
 * sola en la pantalla, en el PDF y en el Excel sin tocar nada aquí.
 *
 * Bajar e imprimir salen del MISMO documento (`_definicionDesglose`): lo que se manda a
 * la impresora es idéntico al archivo que se descarga, no una segunda maqueta que se
 * pueda desincronizar.
 *
 * Forma de una sección (todos los campos son opcionales salvo `titulo`):
 *   { titulo, nota, filas[], tabla{columnas,renglones,totales,vacia}, ramos[], total{} }
 *
 * Dos trampas del proyecto que este archivo respeta:
 *   · pdfmake truena si un `table.body` queda vacío — hay guarda en `_tabla`.
 *   · xlsx-js-style escribe los números como texto si no llevan `t:"n"`; por eso el
 *     Excel usa `celdaMoneda` / `celdaNumero` de estilosExcelNomina y nunca un valor
 *     numérico pelón.
 */

import * as XLSX from 'xlsx-js-style'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import {
  celda, celdaMoneda, celdaNumero,
  headerStyle, textoStyle, monedaStyle, numeroStyle,
  totalTextoStyle, totalMonedaStyle, guiaStyle,
} from '../../ReportesNomina/estilosExcelNomina'

pdfMake.vfs = pdfFonts.pdfMake.vfs

// ─── Formato ─────────────────────────────────────────────────────────────────

export const fmtValor = (valor, formato) => {
  if (valor === null || valor === undefined) return '—'
  if (typeof valor !== 'number') return String(valor)
  switch (formato) {
    case 'porcentaje': return `${valor.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} %`
    case 'dias':
    case 'numero':
      return valor.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 4 })
    default:
      return valor.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
}

const esNumerico = (formato) => formato !== 'texto'

const nombreArchivo = (desglose, linea, prenomina, ext) => {
  const emp = [linea?.apellidoPaterno, linea?.apellidoMaterno, linea?.nombre]
    .filter(Boolean).join(' ') || 'empleado'
  const per = prenomina?.nombre || 'nomina'
  return `${desglose.tipo}_${emp}_${per}`.replace(/[/\\?*[\]:#]/g, '_').slice(0, 90) + ext
}

// Un ramo de IMSS se lee como "base × tasa × días"; con dos tramos se listan los dos.
const textoTramo = (t) =>
  `${fmtValor(t.base, 'moneda')} × ${fmtValor(t.tasa * 100, 'porcentaje')} × ${fmtValor(t.dias, 'dias')} d`

// ─── PDF ─────────────────────────────────────────────────────────────────────

const GRIS = '#5a5a5a'

const _tabla = (widths, body, opciones = {}) => {
  // pdfmake revienta con un body vacío. Pasa de verdad: un empleado sin deducciones
  // deja la sección de deducciones del ISR sin un solo renglón.
  if (!body || body.length === 0) return null
  return {
    table: { headerRows: opciones.headerRows ?? 1, widths, body },
    layout: {
      hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 0.8 : 0.4),
      vLineWidth: () => 0,
      hLineColor: () => '#cccccc',
      paddingTop: () => 3, paddingBottom: () => 3,
      fillColor: (i) => (i === 0 ? '#eef2f8' : null),
    },
    margin: [0, 2, 0, 8],
  }
}

const _seccionPdf = (sec) => {
  const partes = [{ text: sec.titulo, style: 'seccion' }]
  if (sec.nota) partes.push({ text: sec.nota, style: 'nota' })

  if (sec.filas?.length) {
    const body = [[
      { text: 'Concepto', style: 'th' },
      { text: 'Cómo se obtiene', style: 'th' },
      { text: 'Valor', style: 'th', alignment: 'right' },
    ]]
    for (const f of sec.filas) {
      body.push([
        {
          text: [
            { text: f.etiqueta, bold: !!f.destacado },
            ...(f.nota ? [{ text: `\n${f.nota}`, fontSize: 7, color: GRIS, italics: true }] : []),
          ],
        },
        { text: f.formula || (f.variable ? `variable ${f.variable}` : ''), fontSize: 7, color: GRIS },
        { text: fmtValor(f.valor, f.formato), alignment: 'right', bold: !!f.destacado },
      ])
    }
    const t = _tabla(['*', 'auto', 'auto'], body)
    if (t) partes.push(t)
  }

  if (sec.tabla) {
    const { columnas, renglones, totales, vacia } = sec.tabla
    if (!renglones?.length) {
      partes.push({ text: vacia || 'Sin datos.', style: 'nota' })
    } else {
      const body = [columnas.map(c => ({
        text: c.titulo, style: 'th', alignment: esNumerico(c.formato) ? 'right' : 'left',
      }))]
      for (const r of renglones) {
        body.push(columnas.map(c => ({
          text: fmtValor(r[c.campo], c.formato),
          alignment: esNumerico(c.formato) ? 'right' : 'left',
        })))
      }
      if (totales) {
        body.push(columnas.map(c => ({
          text: totales[c.campo] === undefined ? '' : fmtValor(totales[c.campo], c.formato),
          alignment: esNumerico(c.formato) ? 'right' : 'left',
          bold: true,
        })))
      }
      const t = _tabla(columnas.map((c, i) => (i === 1 ? '*' : 'auto')), body)
      if (t) partes.push(t)
    }
  }

  if (sec.ramos?.length) {
    const body = [[
      { text: 'Ramo', style: 'th' },
      { text: 'Fundamento', style: 'th' },
      { text: 'Base × tasa × días', style: 'th' },
      { text: 'Importe', style: 'th', alignment: 'right' },
    ]]
    for (const r of sec.ramos) {
      body.push([
        {
          text: [
            { text: r.etiqueta },
            ...(r.notas?.length ? [{ text: `\n${r.notas.join(' ')}`, fontSize: 7, color: GRIS, italics: true }] : []),
            ...(r.cuadra ? [] : [{ text: '\nEste desglose no coincide con el importe usado por el sistema.', fontSize: 7, color: '#c0392b', bold: true }]),
          ],
        },
        { text: `${r.fundamento}\n${r.tasaTexto}`, fontSize: 7, color: GRIS },
        { text: r.anulado ? '—' : r.tramos.map(textoTramo).join('\n'), fontSize: 7 },
        { text: fmtValor(r.total, 'moneda'), alignment: 'right' },
      ])
    }
    if (sec.total) {
      body.push([
        { text: sec.total.etiqueta, bold: true },
        { text: '', border: [false, false, false, false] },
        { text: '' },
        { text: fmtValor(sec.total.valor, 'moneda'), alignment: 'right', bold: true },
      ])
    }
    const t = _tabla(['*', 'auto', 'auto', 'auto'], body)
    if (t) partes.push(t)
    if (sec.total?.nota) partes.push({ text: sec.total.nota, style: 'nota' })
  }

  return partes
}

const _definicionDesglose = (desglose, empresa) => {
  const contenido = []

  contenido.push({ text: empresa?.nombre || '', style: 'empresa' })
  contenido.push({ text: desglose.titulo, style: 'titulo' })
  contenido.push({
    text: desglose.encabezado.map(e => `${e.etiqueta}: ${typeof e.valor === 'number' ? fmtValor(e.valor, 'moneda') : e.valor}`).join('     '),
    style: 'encabezado',
  })

  if (desglose.avisos?.length) {
    contenido.push({
      table: {
        widths: ['*'],
        body: desglose.avisos.map(a => [{ text: a, fontSize: 8, color: '#7c5a00' }]),
      },
      layout: {
        hLineWidth: () => 0.4, vLineWidth: () => 0.4,
        hLineColor: () => '#ffe58f', vLineColor: () => '#ffe58f',
        fillColor: () => '#fffbe6',
        paddingTop: () => 4, paddingBottom: () => 4,
      },
      margin: [0, 4, 0, 10],
    })
  }

  for (const sec of desglose.secciones) contenido.push(..._seccionPdf(sec))

  return {
    pageSize: 'LETTER',
    pageMargins: [28, 30, 28, 34],
    defaultStyle: { fontSize: 8.5 },
    footer: (pagina, total) => ({
      text: `Página ${pagina} de ${total}   ·   Memoria de cálculo generada por el sistema`,
      alignment: 'center', fontSize: 7, color: GRIS, margin: [0, 8, 0, 0],
    }),
    styles: {
      empresa:    { fontSize: 9, color: GRIS },
      titulo:     { fontSize: 15, bold: true, margin: [0, 2, 0, 2] },
      encabezado: { fontSize: 8, color: GRIS, margin: [0, 0, 0, 6] },
      seccion:    { fontSize: 10, bold: true, margin: [0, 8, 0, 2] },
      nota:       { fontSize: 7.5, italics: true, color: GRIS, margin: [0, 0, 0, 4] },
      th:         { bold: true, fontSize: 8 },
    },
    content: contenido,
  }
}

export const pdfDesglose = (desglose, linea, prenomina, empresa) =>
  pdfMake
    .createPdf(_definicionDesglose(desglose, empresa))
    .download(nombreArchivo(desglose, linea, prenomina, '.pdf'))

/**
 * Manda el desglose a la impresora: abre el visor del navegador con el diálogo de
 * impresión encima (pdfmake le mete un autoPrint al documento).
 *
 * `print()` abre la ventana con window.open de forma SÍNCRONA, así que hay que llamarlo
 * desde el handler del clic para que el navegador lo cuente como gesto del usuario. Si
 * aun así el bloqueador de pop-ups la mata, pdfmake lanza un string; no se atrapa aquí a
 * propósito —el modal lo convierte en un mensaje que dice qué hacer.
 *
 * `linea` y `prenomina` no se usan (imprimir no nombra ningún archivo): van para que los
 * tres botones del modal se llamen exactamente igual.
 */
// eslint-disable-next-line no-unused-vars
export const imprimirDesglose = (desglose, linea, prenomina, empresa) =>
  pdfMake.createPdf(_definicionDesglose(desglose, empresa)).print()

// ─── Excel ───────────────────────────────────────────────────────────────────

const ANCHO = 6 // columnas de la hoja; el resto se rellena para que los bordes cierren

const filaAncha = (celdas) => {
  const f = [...celdas]
  while (f.length < ANCHO) f.push(celda(''))
  return f
}

export const excelDesglose = (desglose, linea, prenomina, empresa) => {
  const aoa = []

  aoa.push(filaAncha([celda(empresa?.nombre || '', { font: { bold: true, sz: 14 } })]))
  aoa.push(filaAncha([celda(desglose.titulo, { font: { bold: true, sz: 12 } })]))
  for (const e of desglose.encabezado) {
    aoa.push(filaAncha([
      celda(e.etiqueta, { font: { bold: true } }),
      typeof e.valor === 'number' ? celdaMoneda(e.valor) : celda(e.valor),
    ]))
  }
  aoa.push([])

  if (desglose.avisos?.length) {
    aoa.push(filaAncha([celda('AVISOS', { font: { bold: true, sz: 11 } })]))
    for (const a of desglose.avisos) aoa.push(filaAncha([celda(a, guiaStyle)]))
    aoa.push([])
  }

  for (const sec of desglose.secciones) {
    aoa.push(filaAncha([celda(sec.titulo, { font: { bold: true, sz: 11 } })]))
    if (sec.nota) aoa.push(filaAncha([celda(sec.nota, guiaStyle)]))

    if (sec.filas?.length) {
      aoa.push(filaAncha([
        celda('Concepto', headerStyle),
        celda('Cómo se obtiene', headerStyle),
        celda('Variable', headerStyle),
        celda('Valor', headerStyle),
      ]))
      for (const f of sec.filas) {
        aoa.push(filaAncha([
          celda(f.etiqueta, f.destacado ? { ...textoStyle, font: { bold: true } } : textoStyle),
          celda(f.formula || '', textoStyle),
          celda(f.variable || '', textoStyle),
          typeof f.valor === 'number'
            ? (f.formato === 'moneda' ? celdaMoneda(f.valor) : celdaNumero(f.valor))
            : celda(f.valor, textoStyle),
        ]))
        if (f.nota) aoa.push(filaAncha([celda('', textoStyle), celda(f.nota, guiaStyle)]))
      }
    }

    if (sec.tabla) {
      const { columnas, renglones, totales, vacia } = sec.tabla
      if (!renglones?.length) {
        aoa.push(filaAncha([celda(vacia || 'Sin datos.', guiaStyle)]))
      } else {
        aoa.push(filaAncha(columnas.map(c => celda(c.titulo, headerStyle))))
        for (const r of renglones) {
          aoa.push(filaAncha(columnas.map(c => (
            esNumerico(c.formato) && typeof r[c.campo] === 'number'
              ? (c.formato === 'moneda' ? celdaMoneda(r[c.campo]) : celdaNumero(r[c.campo]))
              : celda(r[c.campo] ?? '', textoStyle)
          ))))
        }
        if (totales) {
          aoa.push(filaAncha(columnas.map(c => (
            totales[c.campo] === undefined
              ? celda('', totalTextoStyle)
              : (typeof totales[c.campo] === 'number'
                ? celdaMoneda(totales[c.campo], totalMonedaStyle)
                : celda(totales[c.campo], totalTextoStyle))
          ))))
        }
      }
    }

    if (sec.ramos?.length) {
      aoa.push(filaAncha([
        celda('Ramo', headerStyle),
        celda('Fundamento', headerStyle),
        celda('Tramo', headerStyle),
        celda('Base', headerStyle),
        celda('Días', headerStyle),
        celda('Importe', headerStyle),
      ]))
      for (const r of sec.ramos) {
        // Un renglón por tramo: así el Excel se puede sumar y auditar tramo a tramo,
        // que es justo para lo que la gente lo baja.
        const tramos = r.anulado ? [] : r.tramos
        if (tramos.length === 0) {
          aoa.push(filaAncha([
            celda(r.etiqueta, textoStyle), celda(`${r.fundamento} — ${r.tasaTexto}`, textoStyle),
            celda('—', textoStyle), celda('', monedaStyle), celda('', numeroStyle), celdaMoneda(0),
          ]))
        } else {
          tramos.forEach((t, i) => {
            aoa.push(filaAncha([
              celda(i === 0 ? r.etiqueta : '', textoStyle),
              celda(i === 0 ? `${r.fundamento} — ${r.tasaTexto}` : '', textoStyle),
              celda(t.etiqueta, textoStyle),
              celdaMoneda(t.base),
              celdaNumero(t.dias),
              celdaMoneda(t.importe),
            ]))
          })
        }
        aoa.push(filaAncha([
          celda('', textoStyle), celda('', textoStyle),
          celda('Total del ramo', totalTextoStyle), celda('', totalTextoStyle), celda('', totalTextoStyle),
          celdaMoneda(r.total, totalMonedaStyle),
        ]))
        if (!r.cuadra) {
          aoa.push(filaAncha([celda(
            `ATENCIÓN: el desglose de "${r.etiqueta}" no coincide con el importe que usó el sistema `
            + `(${r.esperado}). El importe cobrado es el correcto.`,
            { ...guiaStyle, font: { sz: 8, bold: true, color: { rgb: 'C00000' } } },
          )]))
        }
      }
      if (sec.total) {
        aoa.push(filaAncha([
          celda(sec.total.etiqueta, totalTextoStyle), celda('', totalTextoStyle),
          celda('', totalTextoStyle), celda('', totalTextoStyle), celda('', totalTextoStyle),
          celdaMoneda(sec.total.valor, totalMonedaStyle),
        ]))
        if (sec.total.nota) aoa.push(filaAncha([celda(sec.total.nota, guiaStyle)]))
      }
    }

    aoa.push([])
  }

  const hoja = XLSX.utils.aoa_to_sheet(aoa)
  hoja['!cols'] = [{ wch: 46 }, { wch: 40 }, { wch: 18 }, { wch: 16 }, { wch: 12 }, { wch: 16 }]
  // Sin !autofilter: se traga las filas de totales. Sin !freeze: no hace nada en la
  // versión libre de la librería.
  const libro = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(libro, hoja, desglose.tipo)
  XLSX.writeFile(libro, nombreArchivo(desglose, linea, prenomina, '.xlsx'))
}

// ─── Texto plano (botón Copiar) ──────────────────────────────────────────────

export const textoDesglose = (desglose) => {
  const l = []
  l.push(desglose.titulo)
  l.push(desglose.encabezado.map(e => `${e.etiqueta}: ${typeof e.valor === 'number' ? fmtValor(e.valor, 'moneda') : e.valor}`).join('   '))
  if (desglose.avisos?.length) {
    l.push('')
    desglose.avisos.forEach(a => l.push(`[!] ${a}`))
  }
  for (const sec of desglose.secciones) {
    l.push('')
    l.push(`── ${sec.titulo} ──`)
    if (sec.nota) l.push(`   (${sec.nota})`)
    for (const f of (sec.filas || [])) {
      l.push(`   ${String(f.etiqueta).padEnd(44, '.')} ${fmtValor(f.valor, f.formato)}${f.formula ? `   = ${f.formula}` : ''}`)
    }
    if (sec.tabla?.renglones?.length) {
      const { columnas, renglones, totales } = sec.tabla
      l.push('   ' + columnas.map(c => c.titulo).join(' | '))
      renglones.forEach(r => l.push('   ' + columnas.map(c => fmtValor(r[c.campo], c.formato)).join(' | ')))
      if (totales) l.push('   ' + columnas.map(c => (totales[c.campo] === undefined ? '' : fmtValor(totales[c.campo], c.formato))).join(' | '))
    }
    for (const r of (sec.ramos || [])) {
      l.push(`   ${String(r.etiqueta).padEnd(44, '.')} ${fmtValor(r.total, 'moneda')}   [${r.anulado ? '—' : r.tramos.map(textoTramo).join(' + ')}]`)
    }
    if (sec.total) l.push(`   ${String(sec.total.etiqueta).padEnd(44, '.')} ${fmtValor(sec.total.valor, 'moneda')}`)
  }
  return l.join('\n')
}
